import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import heroImage from "@/assets/hero-bg.jpg";

const Hero = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // Convert YouTube URL to embed format with autoplay and mute parameters
  const videoId = "9KrfBJeJLX8";
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`;

  // Defer iframe injection until after first paint to improve FCP/LCP
  useEffect(() => {
    const idle = (cb: () => void) => {
      const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(cb);
      } else {
        setTimeout(cb, 1500);
      }
    };
    idle(() => setShowVideo(true));
  }, []);

  return (
    <section className="relative h-[70vh] sm:h-[75vh] md:h-[85vh] lg:h-screen flex items-end sm:items-center justify-center overflow-hidden">
      {/* Static Background Image (placeholder / LCP candidate) */}
      <div className={`absolute inset-0 transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}>
        <img
          src={heroImage}
          alt="Professional networking"
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/55 to-background/30" />
      </div>

      {/* YouTube Video Background (deferred) */}
      <div className="absolute inset-0 pointer-events-none">
        {showVideo && (
          <iframe
            src={embedUrl}
            title="Background video"
            className="w-full h-full object-cover scale-[3] sm:scale-[2.2] md:scale-[1.8] lg:scale-[1.5]"
            style={{
              border: 'none',
              pointerEvents: 'none'
            }}
            loading="lazy"
            allow="autoplay; encrypted-media"
            onLoad={() => setVideoLoaded(true)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/55 to-background/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center pb-12 sm:pb-0">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-6 animate-fade-in">
          Where Business Meets <span className="text-primary italic">Community</span>
        </h1>
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-foreground max-w-3xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          We're not your typical networking group. RCN is about more than numbers and referrals; it's about real relationships. We focus on meaningful connections, supporting one another, and making a lasting impact in the community we serve.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <Button size="lg" asChild>
            <a href="/benefits" aria-label="Learn more about RCN membership benefits">Learn More</a>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <a href="/visit-a-meeting">
              Visit a Meeting
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
